import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ErrorState, Field, Loading, PageHeader, Surface } from "@/components/ds";
import { ImageUpload } from "@/components/ds/ImageUpload";
import { PrintersCard } from "@/components/ds/PrintersCard";
import { CustomFieldsLibrary } from "@/components/products/CustomFieldsLibrary";
import { CatalogThemeSettings } from "@/components/settings/CatalogThemeSettings";

const legacySeed = (s) => {
  if (!(s.banner_image || s.banner_title || s.banner_subtitle || s.banner_badge || s.banner_cta_label))
    return [];
  return [
    {
      id: "legacy",
      image: s.banner_image || null,
      badge: s.banner_badge || "",
      title: s.banner_title || "",
      subtitle: s.banner_subtitle || "",
      cta_label: s.banner_cta_label || "",
      cta_link: s.banner_cta_link || "",
      cta2_label: s.banner_cta2_label || "",
      cta2_link: s.banner_cta2_link || "",
      active: s.banner_active !== false,
      order: 0,
    },
  ];
};

export default function Settings() {
  const [form, setForm] = useState(null);
  const [banners, setBanners] = useState([]);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [pwdBusy, setPwdBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setBanner = (i, patch) =>
    setBanners((list) => list.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));

  const addBanner = () =>
    setBanners((list) => [
      ...list,
      {
        id: null,
        image: null,
        badge: "",
        title: "",
        subtitle: "",
        cta_label: "",
        cta_link: "",
        cta2_label: "",
        cta2_link: "",
        active: true,
        order: list.length,
      },
    ]);

  const moveBanner = (i, dir) =>
    setBanners((list) => {
      const next = [...list];
      const target = i + dir;
      if (target < 0 || target >= next.length) return list;
      [next[i], next[target]] = [next[target], next[i]];
      return next;
    });

  const changePassword = async () => {
    if ((pwd.new_password || "").length < 6) {
      toast.error("A nova senha precisa ter pelo menos 6 caracteres.");
      return;
    }
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", pwd);
      setPwd({ current_password: "", new_password: "" });
      toast.success("Senha alterada com sucesso.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPwdBusy(false);
    }
  };

  const load = useCallback(() => {
    setError("");
    api
      .get("/settings")
      .then(({ data }) => {
        setForm(data);
        const list = data.catalog_banners || [];
        setBanners(list.length ? list : legacySeed(data));
      })
      .catch((e) => setError(apiError(e)));
  }, []);

  useEffect(load, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/settings", {
        ...form,
        partner_stall_days: Number(form.partner_stall_days) || 30,
        partner_return_days: Number(form.partner_return_days) || 60,
        catalog_banners: banners.map((b, i) => ({ ...b, id: b.id || null, order: i })),
      });
      setForm(data);
      setBanners(data.catalog_banners || []);
      toast.success("Configurações salvas com sucesso.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!form) return <Loading label="Carregando configurações..." />;

  const catalogUrl = `${window.location.origin}/catalogo`;

  return (
    <div className="space-y-8" data-testid="settings-page">
      <PageHeader title="Configurações" subtitle="Dados usados no catálogo público e nos pagamentos." />

      <form onSubmit={submit} className="grid gap-6 lg:grid-cols-2">
        <Surface className="space-y-4 p-6">
          <h2 className="font-display text-lg">Loja e WhatsApp</h2>
          <Field label="Nome da loja">
            <Input value={form.studio_name || ""} onChange={set("studio_name")} data-testid="settings-studio-name-input" />
          </Field>
          <Field label="WhatsApp (com DDI e DDD)" hint="Ex.: 5511999999999">
            <Input value={form.whatsapp || ""} onChange={set("whatsapp")} className="font-num" data-testid="settings-whatsapp-input" />
          </Field>
          <Field label="Mensagem de destaque do catálogo">
            <Textarea value={form.catalog_message || ""} onChange={set("catalog_message")} rows={2} data-testid="settings-catalog-message-input" />
          </Field>
          <Field label="Instagram (link completo)" hint="Aparece na página de acompanhamento do pedido">
            <Input value={form.instagram || ""} onChange={set("instagram")} data-testid="settings-instagram-input" />
          </Field>
          <div className="rounded-lg border border-white/10 bg-secondary/40 p-3">
            <p className="text-xs text-muted-foreground">Link do seu catálogo público</p>
            <a
              href="/catalogo"
              target="_blank"
              rel="noreferrer"
              data-testid="catalog-public-link"
              className="mt-1 block break-all text-sm text-brand transition-colors hover:text-brand/80"
            >
              {catalogUrl}
            </a>
          </div>
        </Surface>

        <Surface className="space-y-4 p-6">
          <h2 className="font-display text-lg">Recebimento via PIX</h2>
          <Field label="Chave PIX">
            <Input value={form.pix_key || ""} onChange={set("pix_key")} data-testid="settings-pix-key-input" />
          </Field>
          <Field label="Nome do beneficiário">
            <Input value={form.pix_holder || ""} onChange={set("pix_holder")} data-testid="settings-pix-holder-input" />
          </Field>
          <Field label="Cidade do beneficiário" hint="Usada no QR Code PIX. Ex.: PORTO ALEGRE">
            <Input value={form.pix_city || ""} onChange={set("pix_city")} data-testid="settings-pix-city-input" />
          </Field>
          <Field label="Banco">
            <Input value={form.bank || ""} onChange={set("bank")} data-testid="settings-bank-input" />
          </Field>
          <Field label="Tarifa de energia (R$/kWh)" hint="Usada pela Calculadora junto com a potência da impressora">
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.energy_kwh_price ?? ""}
              onChange={set("energy_kwh_price")}
              className="font-num"
              data-testid="settings-energy-input"
            />
          </Field>
          <p className="text-xs text-muted-foreground">
            Esses dados serão usados no QR Code PIX dos fechamentos de parceiros.
          </p>
        </Surface>

        <CustomFieldsLibrary />

        <Surface className="space-y-4 p-6">
          <h2 className="font-display text-lg">Estoque de parceiros</h2>
          <Field
            label="Estoque de parceiros parado após (dias)"
            hint="Conta a partir da última venda ou da última reposição recebida"
          >
            <Input
              type="number"
              min="1"
              step="1"
              value={form.partner_stall_days ?? ""}
              onChange={set("partner_stall_days")}
              className="font-num"
              data-testid="settings-stall-days-input"
            />
          </Field>
          <Field label="Segundo alerta / sugerir devolução após (dias)">
            <Input
              type="number"
              min="1"
              step="1"
              value={form.partner_return_days ?? ""}
              onChange={set("partner_return_days")}
              className="font-num"
              data-testid="settings-return-days-input"
            />
          </Field>
          <p className="text-xs text-muted-foreground">
            Mercadoria recém-recebida nunca aparece como parada: o prazo começa a contar da entrada.
          </p>
        </Surface>

        <Surface className="space-y-4 p-6">
          <h2 className="font-display text-lg">Minha senha</h2>
          <Field label="Senha atual">
            <Input
              type="password"
              value={pwd.current_password}
              onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))}
              data-testid="current-password-input"
            />
          </Field>
          <Field label="Nova senha" hint="Mínimo 6 caracteres">
            <Input
              type="password"
              value={pwd.new_password}
              onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))}
              data-testid="new-password-input"
            />
          </Field>
          <Button
            type="button"
            variant="outline"
            disabled={pwdBusy}
            onClick={changePassword}
            data-testid="change-password-btn"
            className="h-10 rounded-full px-5"
          >
            {pwdBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Alterar senha
          </Button>
        </Surface>

        <Surface className="space-y-4 p-6 lg:col-span-2" data-testid="settings-catalog-theme-card">
          <div>
            <h2 className="font-display text-lg">Personalização do catálogo</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Cores do catálogo público principal da TR3ND.
            </p>
          </div>
          <CatalogThemeSettings studioName={form.studio_name} />
        </Surface>

        <Surface className="space-y-4 p-6 lg:col-span-2" data-testid="settings-banner-card">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-lg">Banners do catálogo</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Vários banners viram um carrossel na loja. Deixe um campo vazio para usar o texto
                padrão.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={addBanner}
              data-testid="add-banner-btn"
              className="h-10 gap-2 rounded-full px-4"
            >
              <Plus className="h-4 w-4" /> Adicionar banner
            </Button>
          </div>

          {banners.length === 0 && (
            <p className="rounded-lg border border-dashed border-white/15 bg-secondary/30 p-4 text-sm text-muted-foreground">
              Nenhum banner cadastrado. A loja mostra o banner padrão.
            </p>
          )}

          <div className="space-y-4">
            {banners.map((b, i) => (
              <div
                key={b.id || i}
                className="rounded-xl border border-white/10 bg-secondary/30 p-4"
                data-testid={`banner-item-${i}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 pb-3">
                  <p className="font-display text-sm">Banner {i + 1}</p>
                  <div className="flex items-center gap-1">
                    <span className="mr-2 text-xs text-muted-foreground">Ativo</span>
                    <Switch
                      checked={b.active !== false}
                      onCheckedChange={(v) => setBanner(i, { active: v })}
                      data-testid={`banner-active-${i}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={i === 0}
                      onClick={() => moveBanner(i, -1)}
                      data-testid={`banner-up-${i}`}
                      className="text-muted-foreground"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={i === banners.length - 1}
                      onClick={() => moveBanner(i, 1)}
                      data-testid={`banner-down-${i}`}
                      className="text-muted-foreground"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setBannerToDelete(i)}
                      data-testid={`banner-delete-${i}`}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  <Field label="Imagem de fundo">
                    <ImageUpload
                      value={b.image}
                      onChange={(p) => setBanner(i, { image: p })}
                      testid={`banner-image-${i}`}
                    />
                  </Field>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Selo (badge)">
                      <Input
                        value={b.badge || ""}
                        onChange={(e) => setBanner(i, { badge: e.target.value })}
                        placeholder="Ex.: Feito na hora"
                        data-testid={`banner-badge-${i}`}
                      />
                    </Field>
                    <Field label="Título">
                      <Input
                        value={b.title || ""}
                        onChange={(e) => setBanner(i, { title: e.target.value })}
                        placeholder="Ex.: Quer revender TR3ND?"
                        data-testid={`banner-title-${i}`}
                      />
                    </Field>
                  </div>
                  <Field label="Subtítulo">
                    <Textarea
                      value={b.subtitle || ""}
                      onChange={(e) => setBanner(i, { subtitle: e.target.value })}
                      rows={2}
                      data-testid={`banner-subtitle-${i}`}
                    />
                  </Field>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Botão 1 · texto">
                      <Input
                        value={b.cta_label || ""}
                        onChange={(e) => setBanner(i, { cta_label: e.target.value })}
                        placeholder="Ver produtos"
                        data-testid={`banner-cta-label-${i}`}
                      />
                    </Field>
                    <Field
                      label="Botão 1 · link"
                      hint="Use #produtos, #kits, /sejaparceiro ou uma URL completa"
                    >
                      <Input
                        value={b.cta_link || ""}
                        onChange={(e) => setBanner(i, { cta_link: e.target.value })}
                        placeholder="#produtos"
                        data-testid={`banner-cta-link-${i}`}
                      />
                    </Field>
                    <Field label="Botão 2 · texto">
                      <Input
                        value={b.cta2_label || ""}
                        onChange={(e) => setBanner(i, { cta2_label: e.target.value })}
                        placeholder="Ver kits"
                        data-testid={`banner-cta2-label-${i}`}
                      />
                    </Field>
                    <Field label="Botão 2 · link">
                      <Input
                        value={b.cta2_link || ""}
                        onChange={(e) => setBanner(i, { cta2_link: e.target.value })}
                        placeholder="#kits"
                        data-testid={`banner-cta2-link-${i}`}
                      />
                    </Field>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Surface>

        <Surface className="space-y-4 p-6 lg:col-span-2" data-testid="settings-catalog-texts-card">
          <div>
            <h2 className="font-display text-lg">Textos do catálogo e da parceria</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Usados quando o banner não tem título/subtítulo próprio e na página pública{" "}
              <a
                href="/sejaparceiro"
                target="_blank"
                rel="noreferrer"
                className="text-brand hover:text-brand/80"
                data-testid="partner-page-link"
              >
                /sejaparceiro
              </a>
              .
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Título principal do catálogo">
              <Input value={form.catalog_title || ""} onChange={set("catalog_title")} data-testid="settings-catalog-title-input" />
            </Field>
            <Field label="Texto do botão/CTA de parceria">
              <Input value={form.partner_cta_label || ""} onChange={set("partner_cta_label")} data-testid="settings-partner-cta-input" />
            </Field>
          </div>
          <Field label="Subtítulo / descrição principal">
            <Textarea value={form.catalog_subtitle || ""} onChange={set("catalog_subtitle")} rows={2} data-testid="settings-catalog-subtitle-input" />
          </Field>
          <Field label="Título do formulário de parceria">
            <Input value={form.partner_form_title || ""} onChange={set("partner_form_title")} data-testid="settings-partner-form-title-input" />
          </Field>
          <Field label="Texto explicativo do formulário">
            <Textarea value={form.partner_form_text || ""} onChange={set("partner_form_text")} rows={3} data-testid="settings-partner-form-text-input" />
          </Field>
          <Field label="Mensagem de sucesso após o envio">
            <Textarea value={form.partner_form_success || ""} onChange={set("partner_form_success")} rows={2} data-testid="settings-partner-form-success-input" />
          </Field>
        </Surface>

        <PrintersCard />

        <div className="lg:col-span-2">
          <Button type="submit" disabled={busy} data-testid="save-settings-btn" className="h-11 rounded-full px-6 font-semibold transition-transform active:scale-95">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar configurações
          </Button>
        </div>
      </form>

      <AlertDialog open={bannerToDelete !== null} onOpenChange={(o) => !o && setBannerToDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="delete-banner-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir este banner?</AlertDialogTitle>
            <AlertDialogDescription>
              Somente o Banner {(bannerToDelete ?? 0) + 1} será removido. Confirme e salve as
              configurações para aplicar na loja.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-banner-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setBanners((list) => list.filter((_, i) => i !== bannerToDelete));
                setBannerToDelete(null);
              }}
              data-testid="confirm-delete-banner-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
